"use client";

import { createContext, useCallback, useContext, useRef, useState, ReactNode } from "react";
import { Button, Input, Modal } from "./ui";

interface PasswordOptions {
  title?: string;
  message?: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
}

// Resolves to the entered password, or null if cancelled.
type PasswordFn = (opts: PasswordOptions) => Promise<string | null>;

const PasswordContext = createContext<PasswordFn>(async () => null);

export function PasswordConfirmProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<PasswordOptions>({});
  const [password, setPassword] = useState("");
  const resolver = useRef<((v: string | null) => void) | null>(null);

  const prompt = useCallback<PasswordFn>((options) => {
    setOpts(options);
    setPassword("");
    setOpen(true);
    return new Promise<string | null>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const settle = useCallback((result: string | null) => {
    setOpen(false);
    resolver.current?.(result);
    resolver.current = null;
  }, []);

  return (
    <PasswordContext.Provider value={prompt}>
      {children}
      <Modal
        open={open}
        onClose={() => settle(null)}
        title={opts.title || "Confirm with password"}
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(null)}>
              Cancel
            </Button>
            <Button
              variant={opts.danger ? "danger" : "primary"}
              disabled={!password.trim()}
              onClick={() => settle(password)}
            >
              {opts.confirmLabel || "Confirm"}
            </Button>
          </>
        }
      >
        {opts.message && <div className="mb-3 text-sm text-gray-600">{opts.message}</div>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (password.trim()) settle(password);
          }}
        >
          <Input
            label="Your password"
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Login password"
          />
        </form>
      </Modal>
    </PasswordContext.Provider>
  );
}

export function usePasswordConfirm() {
  return useContext(PasswordContext);
}
